import {useState, useCallback, useMemo, useRef} from "react";

import programNumberSearchService from "./program-number-search-service";
import useInterval from "../../generic/hooks/use-interval/use-interval";
import useStatus from "../../generic/hooks/use-status/use-status";


const defaultService = programNumberSearchService();

const useQuickSearch = ({onSearch, service = defaultService, focusRef}) => {
  const searchedVal = useRef();
  const [choices, setChoices] = useState();
  const [enterToResolve, setEnterToResolve] = useState();
  const [query, setQuery] = useState();
  const clear = useCallback(() => {
    setQuery("");
    setChoices();
    searchedVal.current = null;
  },[]);

  const {
    status,
    setSavingStatus:searching,
    setErrorStatus:error,
    setResetStatus:doneSearching
  } = useStatus();

  const onKeyPress = useCallback((e) => {
    if (e.key === "Enter") {
      setChoices(cur => {
        if (cur && (cur?.length === 1) && !status.saving && !status.error) {
          onSearch(cur[0]?.programNumber);
          setQuery("")
          return undefined;
        }
        setEnterToResolve({
          waiting:true,
          error:false
        });
        return cur;
      })
    }

  },[status.saving, status.error, onSearch]);
  const onDismissError = useCallback(() => {
    setEnterToResolve(undefined);
    doneSearching();
  },[doneSearching]);

  const setFocus = useCallback(() => {
    if (focusRef?.current) {
      focusRef.current.click();
      focusRef.current.focus();
    }
  },[focusRef]);
  //wait just a second and then change focus
  const {restart:startDelayedFocus, stop:stopDelayedFocus} = useInterval({milliseconds:100, onTrigger:setFocus, autoStart:false})

  const doSearch = useCallback(async(value) => {
    try {
      searching();
      const results = await service.search(value);
      setEnterToResolve(cur => {
        if (cur?.waiting) {
          const length = results?.length ?? 0;
          if (length === 1) {
            onSearch(results[0]?.programNumber);
            setChoices(undefined);
            setQuery("");
            return undefined;
          }
          if (length < 1) {
            return {
              waiting:false,
              error:true
            }
          }
        }
        setChoices(results);
        return undefined;
      })
      doneSearching();
      stopDelayedFocus();
    } catch (e) {
      error(e)
    }

  },[doneSearching, onSearch, error, searching, service, stopDelayedFocus])
  const onExecuteSearch = useCallback(() => {
    setQuery(cur => {
      //prevent duplicate searching the same value (when we blur the search input)
      if (cur && searchedVal.current !== cur && (cur ?? "").trim().length > 3 && !cur.saving) {
        searchedVal.current = cur;
        startDelayedFocus();
        doSearch(cur);
      }
      return cur;

    })
  },[doSearch, startDelayedFocus])

  //const {stop,restart} = useInterval({milliseconds, onTrigger:onExecuteSearch})
  const {restart, stop} = useInterval({milliseconds:300, onTrigger:onExecuteSearch, autoStart:false})


  const onBlur = useCallback((e) => {
    stop();
    onExecuteSearch();
  },[stop, onExecuteSearch]);


  //filterTextInput
  const onChange = useCallback((newQuery) => {
    stopDelayedFocus();
    stop();
    setQuery(newQuery);
    restart();
  },[stop,restart, stopDelayedFocus]);

  const onChoiceClicked = useCallback((choice) => {
    onSearch(choice?.programNumber);
    clear();
  },[onSearch, clear]);


  const calloutOpen = useMemo(() => status.saving || status.error || Array.isArray(choices) ||
    enterToResolve?.error === true || enterToResolve?.waiting === true,
  [choices, enterToResolve, status.error, status.saving]);

  return {
    query,
    onBlur,
    onChange,
    choices,
    status,
    onChoiceClicked,
    calloutOpen,
    clear,
    onKeyPress,
    onDismissError,
    hasError: enterToResolve?.error === true
  }

}

export default useQuickSearch;